import {
  Alert,
  Grid,
  IconButton,
  ToggleButton,
  Typography,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Collapse,
  Button,
  TextField,
} from "@mui/material";
import { Box } from "@mui/system";
import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import CreateAreaModal from "../../../components/Modals/CreateAreaModal.js";
import inventoryClient from "../../../clients/inventoryClient.js";
import { useParams } from "react-router-dom";
import SpecSpinner from "../../../components/SpecSpinner/index.js";
import AreaCountList from "./AreaCountList.js";
import AddProductToCountModal from "../../../components/Modals/AddProductToCountModal/index.js";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import {
  ExpandLessOutlined,
  KeyboardArrowDownOutlined,
} from "@mui/icons-material";

const UnorganizedAreaCountDetails = ({areaCountId}) => {
  const [createAreaModalOpen, toggleCreateAreaModalOpen] = useState(false);
  const [isMobileMode, toggleMobileMode] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [isAddModalOpen, toggleIsAddModalOpen] = useState(false);
  const theme = useTheme();
  const largeScreen = useMediaQuery(theme.breakpoints.up("lg"));
  const [searchText, setSearchText] = useState("");
  const queryClient = useQueryClient();

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  useEffect(() => {
    toggleMobileMode(largeScreen ? false : true);
  }, [largeScreen]);

  let { establishmentid, stockcountid } = useParams();
  const { isLoading, isError, data } = useQuery(
    [
      "areacount",
      {
        establishmentId: establishmentid,
        stockCountId: stockcountid,
        areaCountId: areaCountId,
      },
    ],
    inventoryClient.getAreaCountById,
    {
      enabled: !!establishmentid && !!stockcountid && !!areaCountId,
    }
  );

  const handleUpdateBaseRecordQuantity = (recordId, newQuantity) => {
    queryClient.setQueryData(
      [
        "areacount",
        {
          establishmentId: establishmentid,
          stockCountId: stockcountid,
          areaCountId: areaCountId,
        },
      ],
      (prev) => {
        // Find the index of the record to update
        const recordIndex = prev.areaCount.stockCountRecords.findIndex(
          (x) => x.id === recordId
        );
        
        if (recordIndex === -1) return prev;
        
        // Create a shallow copy of the array
        const newExpectedRecordData = [...prev.areaCount.stockCountRecords];
        
        // Only create a new object for the changed record
        newExpectedRecordData[recordIndex] = {
          ...newExpectedRecordData[recordIndex],
          quantityFound: newQuantity
        };

        return {
          ...prev,
          areaCount: {
            ...prev.areaCount,
            stockCountRecords: newExpectedRecordData
          }
        };
      }
    );
  };

  if (isLoading) {
    return <SpecSpinner open text="loading area..." />;
  }

  const handleCloseAddProductModal = () => {
    toggleIsAddModalOpen(false);
  };

  const handleOpenAddProductModal = () => {
    toggleIsAddModalOpen(true);
  };

  const addProductToAreaCountCallback = () => {
    queryClient.invalidateQueries({
      queryKey: [
        "areacount",
        {
          establishmentId: establishmentid,
          stockCountId: stockcountid,
          areaCountId: areaCountId,
        },
      ],
    });
    queryClient.refetchQueries({
      queryKey: [
        "areacount",
        {
          establishmentId: establishmentid,
          stockCountId: stockcountid,
          areaCountId: areaCountId,
        },
      ],
    });
    toggleIsAddModalOpen(false);
  };

  const handleSearch = (e) => {
    setSearchText(e.target.value);
  };

  if (areaCountId === null || areaCountId === undefined) {
    return null;
  }

  const { areaCount } = data;
  return (
    <>
      <Card
        elevation={0}
        sx={{
          width: "100%",
          padding: 0,
          borderRadius: "8px",
          marginTop: 4,
          border: "1px solid",
          borderColor: (theme) => theme.palette.terrain[400],
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            paddingLeft: 2,
            paddingRight: 2,
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{ marginLeft: 2, fontWeight: 700 }}
          >
            Products not associated to an area
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "flexEnd",
              flexDirection: "row",
              paddingTop: 4,
            }}
          >
            <Button
              variant="outlined"
              onClick={handleOpenAddProductModal}
              sx={{ height: "40px" }}
            >
              <AddOutlinedIcon sx={{ fontSize: 22, marginRight: 1 }} />
              <Typography variant="largeButton">Add Product</Typography>
            </Button>
            {!expanded ? (
              <IconButton
                variant="outlined"
                sx={{
                  margin: 2,
                  width: "32px",
                  height: "32px",
                }}
                onClick={handleExpandClick}
              >
                <KeyboardArrowDownOutlined />
              </IconButton>
            ) : (
              <IconButton
                variant="outlined"
                sx={{
                  margin: 2,
                  width: "32px",
                  height: "32px",
                }}
                onClick={handleExpandClick}
              >
                <ExpandLessOutlined />
              </IconButton>
            )}
          </Box>
        </Box>
        <Collapse in={expanded}>
          <CardContent>
            <Grid item xs={12} sx={{ marginBottom: 6}}>
              <Grid
                container
                direction="column"
                sx={{ marginTop: 4 }}
              >
                {isError && (
                  <Grid item>
                    <Alert severity="error">
                      There was a error trying to load the area count details.
                    </Alert>
                  </Grid>
                )}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    gap: 2,
                    alignItems: { xs: "stretch", md: "center" },
                    marginBottom: 2,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      border: "1px solid",
                      paddingLeft: 2,
                      paddingRight: 1,
                      borderColor: (theme) => theme.palette.terrain[300],
                      backgroundColor: (theme) => theme.palette.pure.white,
                      borderRadius: 2,
                      height: "48px",
                      flexShrink: 0,
                    }}
                  >
                    <Typography variant="body1" sx={{ marginRight: 2, whiteSpace: "nowrap" }}>
                      Input Figures By:
                    </Typography>
                    <ToggleButton
                      selected={isMobileMode}
                      value="slider"
                      onChange={() => toggleMobileMode(!isMobileMode)}
                      sx={{
                        height: "32px",
                        "&.Mui-selected, &.Mui-selected:hover": {
                          color: (theme) => theme.palette.pure.white,
                          backgroundColor: (theme) =>
                            theme.palette.secondary[800],
                        },
                      }}
                    >
                      <Typography variant="body2">Slider</Typography>
                    </ToggleButton>
                    <ToggleButton
                      selected={!isMobileMode}
                      value="keyboard"
                      onChange={() => toggleMobileMode(!isMobileMode)}
                      sx={{
                        height: "32px",
                        "&.Mui-selected, &.Mui-selected:hover": {
                          color: (theme) => theme.palette.pure.white,
                          backgroundColor: (theme) =>
                            theme.palette.secondary[800],
                        },
                      }}
                    >
                      <Typography variant="body2">Keyboard</Typography>
                    </ToggleButton>
                  </Box>
                  <TextField
                    size="small"
                    fullWidth
                    id="productSearch"
                    autoComplete="off"
                    value={searchText}
                    onChange={handleSearch}
                    sx={{
                      backgroundColor: (theme) => theme.palette.pure.white,
                      borderRadius: 1,
                      maxWidth: { md: "400px" },
                      "& .MuiOutlinedInput-root": {
                        height: "48px",
                      },
                      "& .MuiInputLabel-root": {
                        display: "flex",
                        alignItems: "center",
                        transform: "translate(14px, 12px) scale(1)",
                        "&.MuiInputLabel-shrink": {
                          transform: "translate(14px, -9px) scale(0.75)",
                        },
                      },
                    }}
                    label={
                      <>
                        <SearchOutlinedIcon style={{ paddingRight: "5px", verticalAlign: "middle" }} />
                        Search for products...
                      </>
                    }
                  />
                </Box>
              </Grid>
              {areaCount && (
                <AreaCountList
                  areaCountName={areaCount.name}
                  stockCountRecords={areaCount.stockCountRecords?.filter(record => 
                    !searchText || 
                    record.recordName.toLowerCase().includes(searchText.toLowerCase()) ||
                    record.recordSize.toLowerCase().includes(searchText.toLowerCase())
                  ) || []}
                  isMobileMode={isMobileMode}
                  updateBaseRecordQuantity={handleUpdateBaseRecordQuantity}
                  searchText={searchText}
                  searching={!!searchText}
                />
              )}
            </Grid>
          </CardContent>
        </Collapse>
      </Card>

      <AddProductToCountModal
        areaCountId={areaCount?.id}
        establishmentId={establishmentid}
        stockCountId={stockcountid}
        isOpen={isAddModalOpen}
        onSuccessCallback={addProductToAreaCountCallback}
        toggleModalOpen={handleCloseAddProductModal}
        skipAddToAreaOption={true}
      />
      <CreateAreaModal
        isOpen={createAreaModalOpen}
        toggleModalOpen={toggleCreateAreaModalOpen}
      />
    </>
  );
};

export default UnorganizedAreaCountDetails;
